import { useState } from 'react';
import { C } from '@ridgewill/brand';
import { Modal, ModalActions } from './Modal.js';
import { Button } from './ui.js';

/**
 * Tick some of what is not already held, and confirm.
 *
 * ── This was deliberately duplicated, and that has now expired ───────
 *
 * Two byte-identical copies lived on the Security groups screen and the Access
 * tab, with a comment on one of them saying so: *"deliberately a copy rather
 * than a shared import: the two will drift apart the moment either grows a
 * search box or a description column, and forty lines is cheaper than the wrong
 * abstraction."*
 *
 * That was a fair call at two. Neither has drifted, and the Applications screen
 * now wants a third for exactly the same job — picking security groups to grant
 * an application to. Three identical copies is where the arithmetic reverses:
 * the next change to the shape has to be made in three places and will be made
 * in one.
 *
 * The original prediction still stands as the condition for splitting it up
 * again. If one caller needs a search box over hundreds of options, it should
 * get its own component rather than a `searchable` flag here — a picker with two
 * modes is how this becomes the wrong abstraction after all.
 *
 * ── It is a MODAL, and that is not in tension with the refactor ──────
 *
 * Record detail views became screens on 2026-08-28; confirmations and pickers
 * did not. This is a picker: you open it, choose, and leave. See
 * components/Record.tsx.
 */
export function PickList({
  title,
  hint,
  options,
  emptyMessage,
  onClose,
  onConfirm,
}: {
  title: string;
  hint: string;
  options: { value: string; label: string }[];
  /** What to say when there is nothing left to pick. Its own prop rather than an
   *  empty list rendering blank, because "you already have all of these" and
   *  "something failed to load" must not look the same. */
  emptyMessage: string;
  onClose: () => void;
  onConfirm: (picked: string[]) => void;
}) {
  const [picked, setPicked] = useState<string[]>([]);

  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <ModalActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button disabled={picked.length === 0} onClick={() => onConfirm(picked)}>
            Add
          </Button>
        </ModalActions>
      }
    >
      {options.length === 0 ? (
        <p style={{ fontSize: '.88rem', color: C.muted, margin: 0 }}>{emptyMessage}</p>
      ) : (
        <>
          <p style={{ fontSize: '.88rem', color: C.muted, marginTop: 0 }}>{hint}</p>
          <div style={{ display: 'grid', gap: '.5rem' }}>
            {options.map((o) => (
              <label
                key={o.value}
                style={{ display: 'flex', alignItems: 'center', gap: '.45rem', fontSize: '.88rem' }}
              >
                <input
                  type="checkbox"
                  checked={picked.includes(o.value)}
                  onChange={() =>
                    setPicked(
                      picked.includes(o.value)
                        ? picked.filter((v) => v !== o.value)
                        : [...picked, o.value],
                    )
                  }
                />
                {o.label}
              </label>
            ))}
          </div>
        </>
      )}
    </Modal>
  );
}
